import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { canAccess, isStaffRole } from '../lib/permissions';
import { usePublicConfig } from '../lib/publicConfig';

export default function ProtectedRoute({ children, role, forum }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const cfg = usePublicConfig();

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Force users with `must_change_password=true` to land on the
  // change-password page before doing anything else. The change-password
  // page itself is exempted so the user can actually complete the step.
  if (user.must_change_password && !location.pathname.startsWith('/cambiar-contrasena')) {
    return <Navigate to="/cambiar-contrasena" replace />;
  }

  // Forum master switch: when disabled, forum routes are blocked for EVERYONE
  // (including staff). Admins re-enable the forum from the admin panel.
  if (forum && cfg && cfg.forum_enabled === false) {
    return <Navigate to="/mi-perfil" replace />;
  }

  // role can be a string OR array of allowed roles. If omitted, any logged-in
  // user passes. Special sentinels:
  //   'staff'  -> any staff role (everything except members/'interpreter')
  //   'authed' -> any authenticated user (member or staff)
  if (role) {
    if (role === 'authed') {
      return children;
    }
    if (role === 'staff') {
      if (isStaffRole(user.role)) return children;
      return <Navigate to="/es" replace />;
    }
    const allowed = Array.isArray(role) ? role : [role];
    // Any custom staff role is allowed wherever 'council'/'asistente' are.
    const staffAllowed = allowed.includes('council') || allowed.includes('asistente');
    if (!allowed.includes(user.role) && !(staffAllowed && isStaffRole(user.role))) {
      return <Navigate to="/es" replace />;
    }
  }

  return children;
}



/** Permission-based guard for individual admin routes. Allowed when:
 *   - role === 'admin' (super-admin sees everything), OR
 *   - a granular staff role (council, asistente, custom) AND `perm` granted
 * Special: `perm="admin"` means SUPER-ADMIN ONLY (e.g. /admin/users).
 *
 * If unauthorized, redirects to the admin dashboard.
 */
export function AdminGuard({ children, perm }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === 'admin') return children;
  if (perm === 'admin') {
    // Super-admin only
    return <Navigate to="/admin" replace />;
  }
  if (canAccess(user, perm)) {
    return children;
  }
  return <Navigate to="/admin" replace />;
}
